import {
  Button,
  Brand,
  BrandType,
  Disclaimer,
  Div,
  LocationPinSmallIcon,
  P,
  StopIcon,
  TooltipIcon,
  VDriveEntity,
  VDriveOrderStatus,
  VDriveWhiteIcon,
  displayCentsInDollars,
  isAdmin,
  lightTheme,
  removeHyphens,
  OrderBrand,
} from '@vgrubs/components';
import { Dispatch, SetStateAction } from 'react';

const chooseVDriveStatusText = (v: VDriveOrderStatus, cancelledBy: string) => {
  switch (v) {
    case VDriveOrderStatus.ASSIGNED:
      return 'Delivery in progress.';
    case VDriveOrderStatus.CANCELLED:
      return cancelledBy === 'api'
        ? 'Delivery has failed!'
        : 'Delivery cancelled by Merchant.';
    case VDriveOrderStatus.CAN_BE_CANCELLED:
      return 'Assigning vDriver.';
    case VDriveOrderStatus.NOT_ASSIGNED:
      return 'No Driver Assigned.';
    case VDriveOrderStatus.COMPLETED:
      return 'Successful Delivery!';

    default:
      break;
  }
};

const chooseVDriveStatusColor = (v: VDriveOrderStatus) => {
  switch (v) {
    case VDriveOrderStatus.ASSIGNED:
      return lightTheme.transparentBlue;
    case VDriveOrderStatus.CANCELLED:
      return lightTheme.transparentRed;
    case VDriveOrderStatus.CAN_BE_CANCELLED:
      return lightTheme.transparentBlue;
    case VDriveOrderStatus.NOT_ASSIGNED:
      return lightTheme.transparentBlue;
    case VDriveOrderStatus.COMPLETED:
      return lightTheme.transparentGreen;

    default:
      break;
  }
};

const chooseVDriveStatusColorBorder = (v: VDriveOrderStatus) => {
  switch (v) {
    case VDriveOrderStatus.ASSIGNED:
      return lightTheme.darkBlue;
    case VDriveOrderStatus.CANCELLED:
      return lightTheme.primary;
    case VDriveOrderStatus.CAN_BE_CANCELLED:
      return lightTheme.darkBlue;
    case VDriveOrderStatus.NOT_ASSIGNED:
      return lightTheme.darkBlue;
    case VDriveOrderStatus.COMPLETED:
      return lightTheme.green;

    default:
      break;
  }
};

export const VDriveStatusComponent = ({
  handleGetCancelVDriveOrderFee,
  getDrivers,
  setChangeAddressModal,
  m = '0 0 20px 48px',
  mSM = '0 0 20px 0',
  vDrive,
  brand,
  id,
}: {
  handleGetCancelVDriveOrderFee: () => void;
  getDrivers: () => void;
  setChangeAddressModal: Dispatch<SetStateAction<number | null>>;
  m?: string;
  mSM?: string;
  vDrive: VDriveEntity;
  brand: Brand | OrderBrand | null;
  id: number;
}) => {
  const trackingLink = vDrive?.trackingLink ?? '';
  const openTrackingLink = () =>
    window.open(trackingLink, '_blank', 'noopener,noreferrer');

  const isVGrubs = brand?.type === BrandType.vGrubs && !brand?.isLocation;

  const isDisabledVDriveButton = (v: VDriveOrderStatus) => {
    switch (v) {
      case VDriveOrderStatus.ASSIGNED:
        return !trackingLink;
      case VDriveOrderStatus.CANCELLED:
        return isVGrubs && !isAdmin ? !trackingLink : false;
      case VDriveOrderStatus.CAN_BE_CANCELLED:
        return isVGrubs && !isAdmin ? !trackingLink : false;
      case VDriveOrderStatus.NOT_ASSIGNED:
        return isVGrubs && !isAdmin ? !trackingLink : false;
      case VDriveOrderStatus.COMPLETED:
        return !trackingLink;

      default:
        break;
    }
  };

  const chooseVDriveButtonFunction = (v: VDriveOrderStatus) => {
    switch (v) {
      case VDriveOrderStatus.ASSIGNED:
        return openTrackingLink();
      case VDriveOrderStatus.CANCELLED:
        return isVGrubs && !isAdmin ? openTrackingLink() : getDrivers();
      case VDriveOrderStatus.CAN_BE_CANCELLED:
        return isVGrubs && !isAdmin
          ? openTrackingLink()
          : handleGetCancelVDriveOrderFee();
      case VDriveOrderStatus.NOT_ASSIGNED:
        return isVGrubs && !isAdmin ? openTrackingLink() : getDrivers();
      case VDriveOrderStatus.COMPLETED:
        return openTrackingLink();

      default:
        break;
    }
  };

  const chooseVDriveStatusButtonText = (v: VDriveOrderStatus) => {
    switch (v) {
      case VDriveOrderStatus.ASSIGNED:
        return 'Track vDriver';
      case VDriveOrderStatus.CANCELLED:
        return isVGrubs && !isAdmin ? 'Track vDriver' : 'Assign vDriver';
      case VDriveOrderStatus.CAN_BE_CANCELLED:
        return isVGrubs && !isAdmin ? 'Track vDriver' : 'Stop vDriver';
      case VDriveOrderStatus.NOT_ASSIGNED:
        return isVGrubs && !isAdmin ? 'Track vDriver' : 'Assign vDriver';
      case VDriveOrderStatus.COMPLETED:
        return 'See vDrive Delivery';

      default:
        break;
    }
  };

  const chooseVDriveStatusButtonColor = (v: VDriveOrderStatus) => {
    switch (v) {
      case VDriveOrderStatus.ASSIGNED:
        return true;
      case VDriveOrderStatus.CANCELLED:
        return isVGrubs && !isAdmin ? true : true;
      case VDriveOrderStatus.CAN_BE_CANCELLED:
        return isVGrubs && !isAdmin ? true : false;
      case VDriveOrderStatus.NOT_ASSIGNED:
        return isVGrubs && !isAdmin ? true : true;
      case VDriveOrderStatus.COMPLETED:
        return true;

      default:
        break;
    }
  };

  return (
    <Div
      $m={m}
      $mSM={mSM}
      $p="16px"
      $box
      $background={chooseVDriveStatusColor(
        vDrive?.status ?? VDriveOrderStatus.NOT_ASSIGNED
      )}
      $borderColor={chooseVDriveStatusColorBorder(
        vDrive?.status ?? VDriveOrderStatus.NOT_ASSIGNED
      )}
    >
      <Div
        $flex
        $alignCenter
        $gap="16px"
        $gapSM="8px"
        $between
        // $columnSM
      >
        <Div>
          <Div $flex $alignCenter>
            <P $m="0">
              {chooseVDriveStatusText(
                vDrive?.status ?? VDriveOrderStatus.NOT_ASSIGNED,
                vDrive?.cancelledBy ?? 'api'
              )}{' '}
            </P>
            {vDrive?.cancellationReason && (
              <TooltipIcon tooltip={`Reason: ${vDrive?.cancellationReason}`} />
            )}
          </Div>

          {isAdmin &&
            (vDrive?.status === VDriveOrderStatus.NOT_ASSIGNED ||
              vDrive?.status === VDriveOrderStatus.CANCELLED) && (
              <Div
                $flex
                $alignCenter
                $gap="4px"
                onClick={() => setChangeAddressModal(id)}
                $pointer
                $mt="4"
              >
                <LocationPinSmallIcon />
                <Disclaimer $underline $m="0">
                  Wrong Address?
                </Disclaimer>
              </Div>
            )}
          {vDrive?.status === VDriveOrderStatus.ASSIGNED && (
            <Div
              $flex
              $alignCenter
              $gap="4px"
              onClick={() => handleGetCancelVDriveOrderFee()}
              $pointer
              $mt="4"
            >
              <StopIcon />
              <Disclaimer $underline $m="0">
                Stop vDriver
              </Disclaimer>
            </Div>
          )}
        </Div>
        <Button
          style={{
            flex: 'none',
          }}
          $w="auto"
          $wSM="auto"
          $medium
          $darkBlue={chooseVDriveStatusButtonColor(vDrive?.status!)}
          $selected
          onClick={() => chooseVDriveButtonFunction(vDrive?.status!)}
          disabled={isDisabledVDriveButton(vDrive?.status!)}
        >
          <Div $flex $alignCenter $gap="8px">
            <VDriveWhiteIcon /> {chooseVDriveStatusButtonText(vDrive?.status!)}
          </Div>
        </Button>
      </Div>
      {isAdmin ? (
        <SavingsComponent saving={vDrive?.savings ?? null} />
      ) : !isVGrubs ? (
        <SavingsComponent saving={vDrive?.savings ?? null} />
      ) : null}
    </Div>
  );
};

const savingsColor = (v: number | null) => {
  if (v == null) {
    return lightTheme.yellow;
  } else if (v < 0) {
    return lightTheme.darkBlue;
  } else {
    return lightTheme.green;
  }
};

const savingsText = (v: number | null) => {
  if (v == null) {
    return `EST. SAVINGS: CONTRACT PENDING`;
  } else if (v < 0) {
    return `EST. SUBSIDY: ${removeHyphens(displayCentsInDollars(v))}`;
  } else {
    return `EST. SAVINGS: ${displayCentsInDollars(v)}`;
  }
};

const savingsTip = (v: number | null) => {
  if (v == null) {
    return `This contract is pending with the delivery provider, we are still waiting to see how much you will be saving on vDrive!`;
  } else if (v < 0) {
    return `This is the estimated dollar amount you will be subsidizing on this delivery! We have factored in both the delivery fee the restaurant received from the customer, and the commissions reduction you received by switching to vDrive. You may want to consider having your delivery fees adjusted a little higher!`;
  } else {
    return `This is the estimated dollar amount you will be saving on this delivery! We have factored in both the delivery fee the restaurant received from the customer, and the commissions reduction you received by switching to vDrive.`;
  }
};

const SavingsComponent = ({ saving }: { saving: number | null }) => {
  return (
    <Div
      $height="24px"
      $heightSM="24px"
      $width="100%"
      $borderRadius="50px"
      $mt="12"
      $background={savingsColor(saving)}
      $flex
      $alignCenter
      $between
    >
      <div></div>
      <Disclaimer $m="0" $color={lightTheme.white}>
        {savingsText(saving)}{' '}
      </Disclaimer>
      <Div $p="0 4px 0 0">
        <TooltipIcon color={lightTheme.white} tooltip={savingsTip(saving)} />
      </Div>
    </Div>
  );
};
